import {
  storeUserId,
  useBookmarks,
  useBookmarkSelector,
  VisitStatusEnum,
} from 'pages/exhibitor/exhibitorBookmarks';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNetwork } from 'services/network';
import { defaultSearchYear } from 'utils/constants';
import translations from 'utils/translations';
import { FilterContext } from './FilterContextProvider';
import { LoaderContext } from './LoaderContextProvider';

const ExhibitorContext = createContext();

const ExhibitorContextProvider = ({ children, loggedUser }) => {
  const intl = useIntl();
  const i18n = translations.ExhibitorsList(intl).page;
  const network = useNetwork();
  const { listExhibitors, listFolders } = useBookmarks();
  const { addExhibitorToStore, addFolderToStore } = useBookmarkSelector();
  const { filter } = useContext(FilterContext);
  const { setLoaderActive } = useContext(LoaderContext);
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const [contextExhibitor, setContextExhibitor] = useState({});
  const [contextUser, setContextUser] = useState({});
  const [incognitoMode, setIncognitoMode] = useState();
  const [loadChildren, setLoadChildren] = useState(false);

  useEffect(() => {
    if (typeof loggedUser === 'object') {
      if (loggedUser?.email) {
        showLoader();
        setIncognitoMode(false);
      } else {
        setIncognitoMode(true);
        setLoadChildren(true);
      }
    }
  }, [loggedUser]);

  useEffect(() => {
    const fetchExhibitorsToVisit = async () => {
      setIncognitoMode(false);
      showLoader();
      const resStatus = await Promise.all([
        listExhibitors({
          salesforceId: loggedUser.salId,
          status: VisitStatusEnum.TO_VISIT,
          useLoader: false,
        }),
        listExhibitors({
          salesforceId: loggedUser.salId,
          status: VisitStatusEnum.VISITED,
          useLoader: false,
        }),
        listFolders({
          salesforceId: loggedUser.salId,
          useLoader: false,
        }),
      ]);
      const [toVisitArr, visitedArr, folders] = resStatus;
      const tempFolders = {};
      if ([...toVisitArr, ...visitedArr]?.length) {
        const allExhibitorsByStatus = [...toVisitArr, ...visitedArr];
        const params = `anno=${defaultSearchYear}`;
        const accountIds = allExhibitorsByStatus.map((ex) => ex.exhibitor_id);
        const exhibitorsCard = await network.exhibitorSearch(
          params,
          accountIds
        );
        if (exhibitorsCard?.data?.length) {
          const parsed = exhibitorsCard?.data?.map((e) => {
            const currentEx = allExhibitorsByStatus.find(
              (exhi) => exhi.exhibitor_id === e.accountId
            );
            const exhibitorFolder = currentEx?.scheduled_folders
              ? Object.keys(currentEx?.scheduled_folders)[0]
              : undefined;
            if (exhibitorFolder)
              tempFolders[exhibitorFolder] = tempFolders[exhibitorFolder]
                ?.length
                ? [...tempFolders[exhibitorFolder], e.accountId]
                : [e.accountId];
            return {
              name: e.nomeEspositore,
              provincia: e.provinciaSigla,
              appuntamenti: e.appuntamenti || false,
              prenotazioneAppuntamenti: e.prenotazioneAppuntamenti || false,
              emailDigitale: e.emailDigitale || e.email,
              indirizzo: e.indirizzo,
              comune: e.comune,
              email: e.email,
              cap: e.cap,
              categorie_en: e.categorieE,
              categorie_it: e.categorieI,
              prodotti_en: e.prodottiE,
              prodotti_it: e.prodottiI,
              phone: e.telefono,
              fax: e.fax,
              website: e.sitoInternet,
              hall: e.hall,
              stand: e.stand,
              erpid: e.erpId,
              nation: filter.nations.find(
                (n) => n.isoCode === e.nazioneIso3AlphaCode
              )?.label,
              event:
                e.evento
                  ?.split('|')
                  .map(
                    (evento) =>
                      filter.exhibitions.find((ex) => ex.code === evento)
                        ?.label || i18n.notAvailable
                  )
                  .join(', ') || '',
              salesforceId: e.accountId,
              linkedAccountId: e.linkedAccountId,
              status: currentEx.status,
              percorsiTematici: e.percorsiTematici,
            };
          });
          parsed.map((ex) =>
            addExhibitorToStore({
              exhibitor: ex,
            })
          );
        }
      }
      if (folders?.length) {
        folders.map((f) => {
          addFolderToStore({
            folderId: f.folder_id,
            name: f.title,
            exhibitors: tempFolders[f.folder_id] || [],
          });
        });
      }
      storeUserId(loggedUser.salId);
      setLoadChildren(true);
    };
    if (typeof incognitoMode === 'boolean' && !incognitoMode && filter) {
      fetchExhibitorsToVisit();
    }
  }, [incognitoMode, filter]);

  return (
    <ExhibitorContext.Provider
      value={{
        contextExhibitor,
        setContextExhibitor,
        contextUser,
        setContextUser,
        incognitoMode,
      }}
    >
      {loadChildren && children}
    </ExhibitorContext.Provider>
  );
};

export { ExhibitorContextProvider, ExhibitorContext };
